import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { auth, db, functions, httpsCallable } from '../config/firebase';

const PublicAuthContext = createContext(null);

const mapAuthError = error => {
  const code = error?.code || '';
  switch (code) {
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No user found with that email.';
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'Incorrect email or password.';
    case 'auth/email-already-in-use':
      return 'This email address is already in use.';
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please try again later.';
    default:
      return error?.message || 'Authentication error. Please try again.';
  }
};

const resolveEmailFromIdentifier = async identifier => {
  if (!identifier || identifier.includes('@')) {
    return identifier;
  }

  try {
    const callable = httpsCallable(functions, 'resolveUsernameToEmail');
    const response = await callable({ username: identifier });
    const email = response?.data?.email;
    if (!email) {
      throw new Error('Email not associated with this username.');
    }
    return email;
  } catch (error) {
    const errorCode = error?.code || '';
    if (errorCode === 'functions/not-found') {
      throw new Error('Username not found.');
    }
    if (errorCode === 'functions/invalid-argument') {
      throw new Error('Please enter a valid username.');
    }
    throw new Error(
      error?.message || 'Unable to resolve username. Please try again.',
    );
  }
};

export const usePublicAuth = () => {
  const ctx = useContext(PublicAuthContext);
  if (!ctx) {
    throw new Error('usePublicAuth must be used within PublicAuthProvider');
  }
  return ctx;
};

export function PublicAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
      setInitializing(false);
    });
    return () => unsubscribe();
  }, []);

  const login = useCallback(async (identifier, password) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      let email = identifier.trim().toLowerCase();
      email = await resolveEmailFromIdentifier(email);
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      const friendly = mapAuthError(error);
      setAuthError(friendly);
      return { success: false, error: friendly };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const signup = useCallback(async (email, password, username) => {
    setAuthLoading(true);
    setAuthError(null);
    try {
      const usernameLower = username.trim().toLowerCase();

      const usernameDoc = await getDoc(doc(db, 'usernames', usernameLower));
      if (usernameDoc.exists()) {
        throw new Error('Username is already taken.');
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password.trim(),
      );
      await updateProfile(userCredential.user, {
        displayName: username,
      });

      const batchWrites = [
        setDoc(
          doc(db, 'users', userCredential.user.uid),
          {
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            username: usernameLower,
            displayName: username,
            createdAt: serverTimestamp(),
          },
          { merge: true },
        ),
        setDoc(
          doc(db, 'usernames', usernameLower),
          {
            userId: userCredential.user.uid,
            uid: userCredential.user.uid,
            email: userCredential.user.email,
            username: usernameLower,
            createdAt: serverTimestamp(),
          },
          { merge: true },
        ),
      ];
      await Promise.all(batchWrites);
      return { success: true };
    } catch (error) {
      const friendly = mapAuthError(error);
      setAuthError(friendly);
      return { success: false, error: friendly };
    } finally {
      setAuthLoading(false);
    }
  }, []);

  const resetPassword = useCallback(async email => {
    try {
      await sendPasswordResetEmail(auth, email.trim());
      return { success: true };
    } catch (error) {
      return { success: false, error: mapAuthError(error) };
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
  }, []);

  const value = useMemo(
    () => ({
      user,
      initializing,
      authLoading,
      authError,
      login,
      signup,
      resetPassword,
      logout,
    }),
    [
      user,
      initializing,
      authLoading,
      authError,
      login,
      signup,
      resetPassword,
      logout,
    ],
  );

  return (
    <PublicAuthContext.Provider value={value}>
      {children}
    </PublicAuthContext.Provider>
  );
}
